import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Loader2 } from 'lucide-react';
import { API } from '@/utils/api';

// Complete order + dispatch data starts 1 Apr 2026 — earlier FYs are not offered
// (same rule as Product Mix).
const FIRST_COMPLETE_FY = 2026;

// ─── Constants + helpers (mirror RegionProductMix) ───────────
const FABRIC_COLORS = {
  Shirting: '#0EA5E9',
  Suiting:  '#7C3AED',
  Ethnic:   '#EC4899',
  Other:    '#64748B',
  Unclear:  '#F59E0B',
};

const fmtCurrency = (v) => {
  if (v == null || isNaN(v)) return '—';
  const a = Math.abs(v);
  if (a >= 1e7) return `₹${(v / 1e7).toFixed(2)} Cr`;
  if (a >= 1e5) return `₹${(v / 1e5).toFixed(2)} L`;
  if (a >= 1e3) return `₹${(v / 1e3).toFixed(1)} K`;
  return `₹${Math.round(v).toLocaleString('en-IN')}`;
};
const fmtNumber = (v) => (v == null || isNaN(v)) ? '—' : Math.round(v).toLocaleString('en-IN');

const getCurrentFY = () => {
  const now = new Date();
  return now.getMonth() >= 3 ? now.getFullYear() : now.getFullYear() - 1;
};
const fyLabel = (y) => `FY ${y}-${String(y + 1).slice(-2)}`;
const fyMonthOptions = (y) => [
  { v: `${y}-04`, l: `Apr ${y}` },   { v: `${y}-05`, l: `May ${y}` },
  { v: `${y}-06`, l: `Jun ${y}` },   { v: `${y}-07`, l: `Jul ${y}` },
  { v: `${y}-08`, l: `Aug ${y}` },   { v: `${y}-09`, l: `Sep ${y}` },
  { v: `${y}-10`, l: `Oct ${y}` },   { v: `${y}-11`, l: `Nov ${y}` },
  { v: `${y}-12`, l: `Dec ${y}` },
  { v: `${y + 1}-01`, l: `Jan ${y + 1}` },
  { v: `${y + 1}-02`, l: `Feb ${y + 1}` },
  { v: `${y + 1}-03`, l: `Mar ${y + 1}` },
];
const fyQuarterOptions = (y) => [
  { v: `${y}-Q1`, l: `Q1 (Apr–Jun)` },
  { v: `${y}-Q2`, l: `Q2 (Jul–Sep)` },
  { v: `${y}-Q3`, l: `Q3 (Oct–Dec)` },
  { v: `${y}-Q4`, l: `Q4 (Jan–Mar)` },
];


// ─── Reusable card: 4 head-fabric tiles with PYSP + 365d ─────
const HeadCategoryTrendTiles = ({ breakdown }) => {
  if (!breakdown) return null;
  const fabs = ['Shirting', 'Suiting', 'Ethnic', 'Other'];
  // Only surface the Unclear tile when there is something in it — keeps the
  // grid at 4 tiles for divisions that have no unclassified trades.
  const unclear = breakdown['Unclear'] || {};
  if ((unclear.net_value || 0) > 0 || (unclear.pieces || 0) > 0 || (unclear.order_units || 0) > 0) {
    fabs.push('Unclear');
  }
  return (
    <div className={`grid grid-cols-2 gap-3 ${fabs.length > 4 ? 'md:grid-cols-5' : 'md:grid-cols-4'}`}>
      {fabs.map(fab => {
        const b = breakdown[fab] || {};
        const cur = b.pct_value || 0;
        const pysp = b.pct_value_pysp;
        const r90 = b.pct_value_r90;
        const r365 = b.pct_value_r365;
        const color = FABRIC_COLORS[fab];
        return (
          <div
            key={fab}
            className="rounded-lg border p-3"
            style={{ borderColor: `${color}40`, backgroundColor: `${color}0D` }}
          >
            <div className="flex items-center gap-1.5 mb-2">
              <span className="w-2 h-2 rounded-full" style={{ backgroundColor: color }} />
              <span className="text-xs font-semibold uppercase tracking-wide" style={{ color }}>{fab}</span>
            </div>
            <div className="space-y-1.5 text-[11px]">
              <div>
                <div className="text-[9px] uppercase tracking-wide text-gray-500">Value</div>
                <div className="font-bold text-gray-900 text-sm leading-tight">{fmtCurrency(b.net_value || 0)}</div>
                <div className="text-gray-600 leading-tight">{cur.toFixed(1)}% of mix</div>
              </div>
              <div className="grid grid-cols-3 gap-1 pt-1 border-t border-gray-200 text-[10px]">
                <div>
                  <div className="text-gray-500 uppercase tracking-wide text-[8px]">PY</div>
                  <div className="text-gray-800 font-semibold">{pysp != null ? `${pysp.toFixed(1)}%` : '—'}</div>
                </div>
                <div>
                  <div className="text-gray-500 uppercase tracking-wide text-[8px]">90d</div>
                  <div className="text-gray-800 font-semibold">{r90 != null ? `${r90.toFixed(1)}%` : '—'}</div>
                </div>
                <div>
                  <div className="text-gray-500 uppercase tracking-wide text-[8px]">365d</div>
                  <div className="text-gray-800 font-semibold">{r365 != null ? `${r365.toFixed(1)}%` : '—'}</div>
                </div>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

// ─── Top performers table for a fabric category ──────────────
// Pulls trades from overall.dispatches.trades[] filtered by fabric_type.
// `showAll=true` shows every trade in this fabric; `showAll=false`
// shows just the top 10 (default).
const TopPerformersTable = ({ trades, fabric, color, showAll = false }) => {
  const ranked = useMemo(() => {
    const filtered = (trades || [])
      .filter(t => (t.fabric_type || 'Other') === fabric)
      .sort((a, b) => (b.value || 0) - (a.value || 0));
    return showAll ? filtered : filtered.slice(0, 10);
  }, [trades, fabric, showAll]);

  if (ranked.length === 0) {
    return (
      <div className="text-sm text-gray-500 py-6 text-center bg-gray-50 rounded-md">
        No {fabric.toLowerCase()} trades in this period.
      </div>
    );
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-[11px] border-collapse">
        <thead>
          <tr className="text-gray-600 uppercase tracking-wide text-[9px] border-b border-gray-200 bg-gray-50">
            <th className="py-2 pl-2 text-left">#</th>
            <th className="py-2 text-left">Trade</th>
            <th className="py-2 text-right">Metres</th>
            <th className="py-2 text-right">₹ Current</th>
            <th className="py-2 text-right">% Current</th>
            <th className="py-2 text-right" title="Previous-year same-period ₹">₹ PY</th>
            <th className="py-2 text-right" title="Previous-year same-period share">% PY</th>
            <th className="py-2 text-right" title="Year-over-year ₹ change">Δ ₹</th>
            <th className="py-2 text-right" title="Rolling 90-day share">90d %</th>
            <th className="py-2 text-right pr-2" title="Rolling 365-day share">365d %</th>
          </tr>
        </thead>
        <tbody>
          {ranked.map((t, i) => {
            const cur = t.value || 0;
            const pysp = t.value_pysp;
            const dRs = (pysp != null) ? (cur - pysp) : null;
            return (
              <tr key={t.trade_name} className="border-b border-gray-100 hover:bg-gray-50/50">
                <td className="py-1.5 pl-2 text-gray-500">{i + 1}</td>
                <td className="py-1.5 font-medium text-gray-800" style={{ borderLeft: `3px solid ${color}` }}>
                  <span className="pl-2">{t.trade_name}</span>
                </td>
                <td className="py-1.5 text-right">{fmtNumber(t.qty)}</td>
                <td className="py-1.5 text-right font-semibold">{fmtCurrency(cur)}</td>
                <td className="py-1.5 text-right text-gray-600">{(t.pct_value || 0).toFixed(1)}%</td>
                <td className="py-1.5 text-right text-gray-600">{pysp != null ? fmtCurrency(pysp) : '—'}</td>
                <td className="py-1.5 text-right text-gray-600">{t.pct_value_pysp != null ? `${t.pct_value_pysp.toFixed(1)}%` : '—'}</td>
                <td className="py-1.5 text-right font-medium" style={{ color: dRs == null ? '#94A3B8' : (dRs >= 0 ? '#15803D' : '#B91C1C') }}>
                  {dRs != null ? `${dRs >= 0 ? '+' : ''}${fmtCurrency(Math.abs(dRs)).replace('₹', dRs < 0 ? '-₹' : '₹')}` : '—'}
                </td>
                <td className="py-1.5 text-right text-gray-600">{t.pct_value_r90 != null ? `${t.pct_value_r90.toFixed(1)}%` : '—'}</td>
                <td className="py-1.5 text-right pr-2 text-gray-600">{t.pct_value_r365 != null ? `${t.pct_value_r365.toFixed(1)}%` : '—'}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

// ─── Price-band trend table (Shirting + Suiting only) ────────
const PriceBandTrendTable = ({ priceBands, color }) => {
  const bands = priceBands?.bands || [];
  if (!bands.length) return null;
  return (
    <div className="overflow-x-auto mt-3">
      <table className="w-full text-[11px] border-collapse">
        <thead>
          <tr className="text-gray-600 uppercase tracking-wide text-[9px] border-b border-gray-200 bg-gray-50">
            <th className="py-2 pl-2 text-left">Price band</th>
            <th className="py-2 text-right">Lines</th>
            <th className="py-2 text-right">Value</th>
            <th className="py-2 text-right">% Current</th>
            <th className="py-2 text-right">% PY</th>
            <th className="py-2 text-right">90d %</th>
            <th className="py-2 text-right pr-2">365d %</th>
          </tr>
        </thead>
        <tbody>
          {bands.map((b, i) => (
            <tr key={i} className="border-b border-gray-100">
              <td className="py-1.5 pl-2 font-medium" style={{ color }}>{b.band}</td>
              <td className="py-1.5 text-right">{fmtNumber(b.lines)}</td>
              <td className="py-1.5 text-right font-semibold">{fmtCurrency(b.value)}</td>
              <td className="py-1.5 text-right text-gray-600">{(b.pct_value || 0).toFixed(1)}%</td>
              <td className="py-1.5 text-right text-gray-600">{b.pct_value_pysp != null ? `${b.pct_value_pysp.toFixed(1)}%` : '—'}</td>
              <td className="py-1.5 text-right text-gray-600">{b.pct_value_r90 != null ? `${b.pct_value_r90.toFixed(1)}%` : '—'}</td>
              <td className="py-1.5 text-right pr-2 text-gray-600">{b.pct_value_r365 != null ? `${b.pct_value_r365.toFixed(1)}%` : '—'}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

// ─── Period summary card (totals current vs PYSP vs 365d) ────
const PeriodSummary = ({ breakdown, data }) => {
  if (!breakdown) return null;
  const fabs = ['Shirting', 'Suiting', 'Ethnic', 'Other', 'Unclear'];
  // Sum a numeric field across fabrics. Returns null when EVERY
  // fabric has null for that field (signals 'no data exists for
  // this comparison period in this division', so the tile should
  // render '—' instead of '₹0'). Otherwise sums the non-null values.
  const sumOrNull = (key) => {
    let total = 0;
    let anyValue = false;
    for (const f of fabs) {
      const v = (breakdown[f] || {})[key];
      if (v != null) { total += v; anyValue = true; }
    }
    return anyValue ? total : null;
  };
  const totalCur = sumOrNull('net_value') || 0;
  const totalPysp = sumOrNull('net_value_pysp');
  const totalR90 = sumOrNull('net_value_r90');
  const totalR365 = sumOrNull('net_value_r365');
  const yoy = (totalPysp != null && totalPysp > 0)
    ? ((totalCur - totalPysp) / totalPysp * 100)
    : null;
  return (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
      <div className="rounded-lg border border-indigo-200 bg-indigo-50/40 p-3">
        <div className="text-[10px] uppercase tracking-wide text-indigo-700 font-semibold">Current period</div>
        <div className="text-lg font-bold text-gray-900">{fmtCurrency(totalCur)}</div>
        <div className="text-[10px] text-gray-500">Total dispatched value</div>
      </div>
      <div className="rounded-lg border border-amber-200 bg-amber-50/40 p-3">
        <div className="text-[10px] uppercase tracking-wide text-amber-700 font-semibold">Previous year (same period)</div>
        <div className="text-lg font-bold text-gray-900">{totalPysp == null ? '—' : fmtCurrency(totalPysp)}</div>
        <div className="text-[10px]" style={{ color: yoy == null ? '#94A3B8' : (yoy >= 0 ? '#15803D' : '#B91C1C') }}>
          {yoy == null ? 'No PY data for this division' : `${yoy >= 0 ? '+' : ''}${yoy.toFixed(1)}% YoY`}
        </div>
      </div>
      <div className="rounded-lg border border-cyan-200 bg-cyan-50/40 p-3">
        <div className="text-[10px] uppercase tracking-wide text-cyan-700 font-semibold">Rolling 90 days</div>
        <div className="text-lg font-bold text-gray-900">{totalR90 == null ? '—' : fmtCurrency(totalR90)}</div>
        <div className="text-[10px] text-gray-500">Last 3 months (ending current period)</div>
      </div>
      <div className="rounded-lg border border-emerald-200 bg-emerald-50/40 p-3">
        <div className="text-[10px] uppercase tracking-wide text-emerald-700 font-semibold">Rolling 365 days</div>
        <div className="text-lg font-bold text-gray-900">{totalR365 == null ? '—' : fmtCurrency(totalR365)}</div>
        <div className="text-[10px] text-gray-500">Last 12 months (ending current period)</div>
      </div>
    </div>
  );
};

// ─── Per-category section ────────────────────────────────────
const CategorySection = ({ fabric, breakdown, allTrades, showPriceBands = false }) => {
  const [showAll, setShowAll] = useState(false);
  const b = breakdown[fabric] || {};
  const color = FABRIC_COLORS[fabric];
  const hasData = (b.net_value || 0) > 0 || (b.net_value_pysp || 0) > 0;
  // Total count of trades in this fabric across the current period, so
  // the toggle can show how many extra rows 'Show all' would reveal.
  const fabricTradeCount = (allTrades || []).filter(t => (t.fabric_type || 'Other') === fabric).length;
  if (!hasData) {
    return (
      <Card className="border-gray-200 shadow-sm">
        <CardHeader className="pb-3 border-b border-gray-100" style={{ borderLeft: `4px solid ${color}` }}>
          <CardTitle className="text-base font-semibold text-gray-800">{fabric}</CardTitle>
        </CardHeader>
        <CardContent className="py-6 text-sm text-gray-500 text-center">No data for this period.</CardContent>
      </Card>
    );
  }
  return (
    <Card className="border-gray-200 shadow-sm">
      <CardHeader className="pb-3 border-b border-gray-100" style={{ borderLeft: `4px solid ${color}` }}>
        <CardTitle className="flex items-center justify-between text-base font-semibold text-gray-800">
          <div className="flex items-center gap-2">
            <span style={{ color }}>{fabric}</span>
            <span className="text-xs font-normal text-gray-500">
              · {fmtCurrency(b.net_value || 0)} · {(b.pct_value || 0).toFixed(1)}% of mix
            </span>
          </div>
          <div className="text-xs font-normal text-gray-500">
            {fabricTradeCount} trade{fabricTradeCount === 1 ? '' : 's'}
          </div>
        </CardTitle>
      </CardHeader>
      <CardContent className="pt-4 space-y-4">
        <div>
          <div className="flex items-center justify-between mb-2">
            <div className="text-xs font-semibold text-gray-700">
              {showAll ? `All ${fabricTradeCount} performers` : `Top 10 performers`}
            </div>
            {fabricTradeCount > 10 && (
              <button
                onClick={() => setShowAll(s => !s)}
                className="text-[11px] px-2 py-1 rounded border border-gray-300 hover:bg-gray-50 text-gray-700"
              >
                {showAll ? 'Show top 10' : `Show all ${fabricTradeCount}`}
              </button>
            )}
          </div>
          <TopPerformersTable trades={allTrades} fabric={fabric} color={color} showAll={showAll} />
        </div>
        {showPriceBands && b.price_bands && (
          <div className="pt-3 border-t border-gray-100">
            <div className="text-xs font-semibold text-gray-700 mb-1">Price-band trends</div>
            <PriceBandTrendTable priceBands={b.price_bands} color={color} />
          </div>
        )}
      </CardContent>
    </Card>
  );
};

// ═══════════════════════════════════════════════════════════
const DIVISIONS = ['Arvind Tresca DTR', 'Arvind Tresca WS', 'GG DTR', 'GG WS', 'TAS'];

export default function TrendAnalysis({ selectedDivision }) {
  const currentFY = getCurrentFY();
  const fyYears = Array.from({ length: Math.max(1, currentFY - FIRST_COMPLETE_FY + 1) }, (_, i) => currentFY - i);
  const [periodType, setPeriodType] = useState('fy');
  const [periodValue, setPeriodValue] = useState(String(currentFY));
  // Local division selector — defaults to whatever the parent Sales
  // tab has selected, but can be overridden in-page (including 'all'
  // for a consolidated view across every division).
  const [divOverride, setDivOverride] = useState(selectedDivision || 'all');
  useEffect(() => { setDivOverride(selectedDivision || 'all'); }, [selectedDivision]);
  const effectiveDivision = divOverride;

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      try {
        const params = { period_type: periodType, period_value: periodValue, include_trends: true };
        if (effectiveDivision && effectiveDivision !== 'all') params.division = effectiveDivision;
        const res = await axios.get(`${API}/analytics/region-product-mix`, { params, withCredentials: true });
        if (!cancelled) setData(res.data);
      } catch (err) {
        if (!cancelled) setData(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [effectiveDivision, periodType, periodValue]);

  const overall = data?.overall;
  const breakdown = overall?.fabric_breakdown;
  const allTrades = overall?.dispatches?.trades || [];

  return (
    <div className="space-y-4">
      {/* Toolbar */}
      <Card className="border-gray-200 shadow-sm">
        <CardContent className="pt-4 pb-4 flex flex-wrap items-end gap-3">
          <div>
            <label className="text-[10px] uppercase tracking-wide text-gray-500 font-semibold block mb-1">Division</label>
            <Select value={divOverride} onValueChange={setDivOverride}>
              <SelectTrigger className="h-8 text-xs w-[180px]"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All divisions (consolidated)</SelectItem>
                {DIVISIONS.map(d => <SelectItem key={d} value={d}>{d}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wide text-gray-500 font-semibold block mb-1">Period</label>
            <Select value={periodType} onValueChange={v => {
              setPeriodType(v);
              if (v === 'fy') setPeriodValue(String(currentFY));
              if (v === 'month') setPeriodValue(`${currentFY}-04`);
              if (v === 'quarter') setPeriodValue(`${currentFY}-Q1`);
            }}>
              <SelectTrigger className="h-8 text-xs w-[140px]"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="month">Month</SelectItem>
                <SelectItem value="quarter">Quarter</SelectItem>
                <SelectItem value="fy">Financial Year</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wide text-gray-500 font-semibold block mb-1">Value</label>
            {periodType === 'fy' ? (
              <Select value={periodValue} onValueChange={setPeriodValue}>
                <SelectTrigger className="h-8 text-xs w-[160px]"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {fyYears.map(y =>
                    <SelectItem key={y} value={String(y)}>{fyLabel(y)}</SelectItem>
                  )}
                </SelectContent>
              </Select>
            ) : periodType === 'quarter' ? (
              <Select value={periodValue} onValueChange={setPeriodValue}>
                <SelectTrigger className="h-8 text-xs w-[200px]"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {fyYears.flatMap(y => fyQuarterOptions(y).map(opt =>
                    <SelectItem key={opt.v} value={opt.v}>{opt.l} {fyLabel(y)}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            ) : (
              <Select value={periodValue} onValueChange={setPeriodValue}>
                <SelectTrigger className="h-8 text-xs w-[160px]"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {fyYears.flatMap(y => fyMonthOptions(y).map(opt =>
                    <SelectItem key={opt.v} value={opt.v}>{opt.l}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </div>
          <div className="ml-auto text-[10px] text-gray-500 text-right">
            <div>Comparing <strong>{data?.period?.value || '—'}</strong> vs <strong>{data?.pysp_period_value || '—'}</strong></div>
            {data?.r90_months?.length > 0 && (
              <div>90d rolling: {data.r90_months[0]} → {data.r90_months[data.r90_months.length - 1]}</div>
            )}
            {data?.r365_months?.length > 0 && (
              <div>365d rolling: {data.r365_months[0]} → {data.r365_months[data.r365_months.length - 1]}</div>
            )}
          </div>
        </CardContent>
      </Card>

      {loading ? (
        <div className="flex items-center justify-center py-16">
          <Loader2 className="animate-spin text-indigo-500" size={28} />
        </div>
      ) : !breakdown ? (
        <Card className="border-gray-200 shadow-sm">
          <CardContent className="py-16 text-center text-sm text-gray-500">
            No trend data for this division/period.
          </CardContent>
        </Card>
      ) : (
        <>
          {/* Summary */}
          <Card className="border-gray-200 shadow-sm">
            <CardHeader className="pb-3 border-b border-gray-100">
              <CardTitle className="text-base font-semibold text-gray-800">Period summary</CardTitle>
            </CardHeader>
            <CardContent className="pt-4 space-y-4">
              <PeriodSummary breakdown={breakdown} data={data} />
              <HeadCategoryTrendTiles breakdown={breakdown} />
            </CardContent>
          </Card>

          {/* Per-category sections */}
          <CategorySection fabric="Shirting" breakdown={breakdown} allTrades={allTrades} showPriceBands />
          <CategorySection fabric="Suiting" breakdown={breakdown} allTrades={allTrades} showPriceBands />
          <CategorySection fabric="Ethnic" breakdown={breakdown} allTrades={allTrades} />
          <CategorySection fabric="Other" breakdown={breakdown} allTrades={allTrades} />
          {/* Unclear — only when the division actually has unclassified trades */}
          {((breakdown['Unclear'] || {}).net_value || 0) > 0 && (
            <CategorySection fabric="Unclear" breakdown={breakdown} allTrades={allTrades} />
          )}
        </>
      )}
    </div>
  );
}
